import {
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    SelectChangeEvent,
    Tooltip,
    Stack
} from '@mui/material';
import { ChangeEvent } from 'react';
import { BackgroundStyles } from 'types';
import { ImageUpload } from 'components/image-upload';
import { ColorPickerButton } from './color-picker-button';

interface Props {
    backgroundStyles?: BackgroundStyles;
    onBackgroundStylesChange: (backgroundStyles: BackgroundStyles) => void;
    getUploadBackgroundImageAction: (action?: () => Promise<string | undefined>) => void;
}

export const EditBackgroundStyles: React.FC<Props> = ({ backgroundStyles, onBackgroundStylesChange, getUploadBackgroundImageAction }) => {
    const onBackgroundColorChange = (colorHex: string) => {
        onBackgroundStylesChange({
            ...backgroundStyles,
            backgroundColor: colorHex
        });
    };

    const onBckRepeatChange = (event: SelectChangeEvent<string>) => {
        onBackgroundStylesChange({
            ...backgroundStyles,
            backgroundRepeat: event.target.value
                ? (event.target.value as 'repeat' | 'repeat-x' | 'repeat-y' | 'no-repeat' | 'space' | 'round')
                : undefined
        });
    };

    const onBckAttachmentChange = (event: SelectChangeEvent<string>) => {
        onBackgroundStylesChange({
            ...backgroundStyles,
            backgroundAttachment: event.target.value
                ? (event.target.value as 'fixed' | 'scroll' | 'local' | 'revert' | 'inherit')
                : undefined
        });
    };
    const onBckSizeChange = (event: SelectChangeEvent<string>) => {
        onBackgroundStylesChange({
            ...backgroundStyles,
            backgroundSize: event.target.value
                ? (event.target.value as 'auto' | 'unset' | 'contain' | 'cover' | 'inherit' | 'revert' | 'initial')
                : undefined
        });
    };

    const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const fieldName = event.target.getAttribute('data-field') as keyof BackgroundStyles;

        onBackgroundStylesChange({
            ...backgroundStyles,
            [fieldName]: event.target.value as string
        });
    };

    return (
        <>
            <Box maxWidth={'80%'} sx={{ display: 'flex', alignItems: 'center', margin: '0 20px', flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {/* TODO - add option to select gradient using
                             - https://gradient-package-demo.web.app/ -> react-best-gradient-color-picker
                            OR
                             - react-color-gradient-picker - https://github.com/arthay/react-color-gradient-picker
                        */}
                        <ColorPickerButton
                            colorHex={backgroundStyles?.backgroundColor}
                            label="Pick a background color"
                            onColorChange={onBackgroundColorChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', display: 'flex' }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Set an image as the background
                            </Typography>
                            <ImageUpload
                                getUploadAction={getUploadBackgroundImageAction}
                                uploadedImageUrl={backgroundStyles?.backgroundImageUrl}
                            />
                        </Stack>
                    </Grid>
                    {/* <Grid item xs={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            TODO - Create a background gradient
                        </Typography>
                    </Grid> */}
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel id="lbl-bck-size">Background size</InputLabel>
                            <Select
                                labelId="lbl-bck-size"
                                id="ddl-bck-size"
                                value={backgroundStyles?.backgroundSize ?? ''} // Note: MUI Select has an issue with undefined/null values so we need to set and empty string here when genre is not defined. https://github.com/mui/material-ui/issues/4991
                                label="Background size"
                                autoWidth
                                onChange={onBckSizeChange}
                            >
                                <MenuItem value={''}>
                                    <em>Unset</em>
                                </MenuItem>
                                <MenuItem value={'auto'}>
                                    <Tooltip placement="right" title="Scales the background image in the corresponding direction such that its intrinsic proportions are maintained.">
                                        <span>Auto</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'contain'}>
                                    <Tooltip placement="right" title="Scales the image as large as possible within its container without cropping or stretching the image. If the container is larger than the image, this will result in image tiling, unless the background-repeat property is set to no-repeat.">
                                        <span>Contain</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'cover'}>
                                    <Tooltip placement="right" title="Scales the image (while preserving its ratio) to the smallest possible size to fill the container (that is: both its height and width completely cover the container), leaving no empty space. If the proportions of the background differ from the element, the image is cropped either vertically or horizontally.">
                                        <span>Cover</span>
                                    </Tooltip>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel id="lbl-bck-repeat">Background repeat</InputLabel>
                            <Select
                                labelId="lbl-bck-repeat"
                                id="ddl-bck-repeat"
                                value={backgroundStyles?.backgroundRepeat ?? ''} // Note: MUI Select has an issue with undefined/null values so we need to set and empty string here when genre is not defined. https://github.com/mui/material-ui/issues/4991
                                label="Background repeat"
                                autoWidth
                                onChange={onBckRepeatChange}
                            >
                                <MenuItem value={''}>
                                    <em>Unset</em>
                                </MenuItem>
                                <MenuItem value={'repeat'}>
                                    <Tooltip placement="right" title="Tile the image in both directions. This is the default value.">
                                        <span>Repeat</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'repeat-x'}>
                                    <Tooltip placement="right" title="Tile the image horizontally">
                                        <span>Repeat X Axis only</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'repeat-y'}>
                                    <Tooltip placement="right" title="Tile the image vertically">
                                        <span>Repeat Y Axis only</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'no-repeat'}>
                                    <Tooltip placement="right" title="Don't tile, just show the image once">
                                        <span>No Repeat</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'space'}>
                                    <Tooltip
                                        placement="right"
                                        title="Tile the image in both directions. Never crop the image unless a single image is too large to fit. If multiple images can fit, space them out evently images always touching the edges."
                                    >
                                        <span>Space</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'round'}>
                                    <Tooltip
                                        placement="right"
                                        title="Tile the image in both directions. Never crop the image unless a single image is too large to fit. If multiple images can fit with leftover space, squish them or stretch them to fill the space. If it's less than half one image width left, stretch, if it's more, stretch."
                                    >
                                        <span>Round</span>
                                    </Tooltip>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            {/* Move a background image (or gradient) around within its container
                            It has three different types of values:
                                Length values (e.g. 100px 5px)
                                Percentages (e.g. 100% 5%)
                                Keywords (e.g. bottom top center left right revert inherit) 
                            */}
                            <Tooltip
                                placement="right"
                                title="Move a background image (or gradient) around within its container.
                                        It has three different types of values:
                                            Length values (e.g. 100px 5px)
                                            Percentages (e.g. 100% 5%)
                                            Keywords (e.g. bottom top center left right revert inherit)"
                            >
                                <TextField
                                    id="txt-bck-position"
                                    label="Background position"
                                    fullWidth
                                    onChange={onTextChange}
                                    value={backgroundStyles?.backgroundPosition ?? ''}
                                    inputProps={{ 'data-field': 'backgroundPosition' }}
                                    InputLabelProps={{ shrink: !!backgroundStyles?.backgroundPosition }}
                                />
                            </Tooltip>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel id="lbl-bck-attachment">Background repeat</InputLabel>
                            <Select
                                labelId="lbl-bck-attachment"
                                id="ddl-bck-attachment"
                                value={backgroundStyles?.backgroundAttachment ?? ''} // Note: MUI Select has an issue with undefined/null values so we need to set and empty string here when genre is not defined. https://github.com/mui/material-ui/issues/4991
                                label="Background attachment"
                                autoWidth
                                onChange={onBckAttachmentChange}
                            >
                                <MenuItem value={''}>
                                    <em>Unset</em>
                                </MenuItem>
                                <MenuItem value={'scroll'}>
                                    <Tooltip
                                        placement="right"
                                        title="scroll is the default value. It scrolls with the main view, but stays fixed inside the local view."
                                    >
                                        <span>Scroll</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'fixed'}>
                                    <Tooltip
                                        placement="right"
                                        title="fixed stays fixed no matter what. It's kind of like a physical window: moving around the window changes your perspective, but it doesn't change where things are outside of the window."
                                    >
                                        <span>Fixed</span>
                                    </Tooltip>
                                </MenuItem>
                                <MenuItem value={'local'}>
                                    <Tooltip
                                        placement="right"
                                        title="local was invented because the default scroll value acts like a fixed background. It scrolls both with the main view and the local view."
                                    >
                                        <span>Local</span>
                                    </Tooltip>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
